import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Sun, VideoCamera, Lightning, BatteryCharging, Browsers,
  Phone, WhatsappLogo, Star, ShieldCheck, Wrench, Lightbulb, Buildings, Quotes, Check
} from "@phosphor-icons/react";
import { COMPANY, IMAGES, DIVISIONS, PROJECTS, TESTIMONIALS, DEALER_BRANDS } from "@/data/site";
import LeadForm from "@/components/LeadForm";

const ICONS = { Sun, VideoCamera, Lightning, BatteryCharging, Browsers };

const Stagger = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

// Signature on-load: masked line-by-line reveal
const revealLine = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};
const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" variants={revealLine} initial="hidden" animate="show" custom={i}>
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const heroSlides = [
    { src: IMAGES.hero, label: "Solar Energy" },
    { src: IMAGES.cctv, label: "Security Solutions" },
    { src: IMAGES.ev, label: "Electric Mobility" },
    { src: IMAGES.power, label: "Power Backup" },
    { src: IMAGES.digital, label: "Digital Infrastructure" },
  ];
  const [slide, setSlide] = useState(0);
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroContentY = useTransform(scrollY, [0, 700], [0, -40]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 4500);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white min-h-[92vh] flex items-center" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: heroImgY }}>
          <AnimatePresence mode="sync">
            <motion.img
              key={slide}
              src={heroSlides[slide].src}
              alt={heroSlides[slide].label}
              initial={{ opacity: 0, scale: 1.12 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-[120%] object-cover"
              data-testid={`hero-slide-${slide}`}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />
          <div className="grain-overlay" />
        </motion.div>
        <motion.div style={{ y: heroContentY }} className="relative w-full max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-16 lg:pt-24 lg:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 label-eyebrow text-slate-700">
              <Lightning size={14} weight="fill" className="text-amber-500" /> 13+ Years of Engineering Excellence
            </div>
          </motion.div>
          <h1 className="mt-5 font-heading font-black text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.92] tracking-tighter text-slate-950 max-w-4xl">
            <MaskLine i={0}>Powering Your World.</MaskLine>
            <MaskLine i={1}><span className="italic font-light">Intelligently.</span></MaskLine>
          </h1>
          <Stagger delay={0.1}>
            <p className="mt-6 font-heading text-xl lg:text-2xl font-semibold text-slate-900 max-w-2xl">
              Tamil Nadu's Trusted Digital Energy &amp; Security Partner
            </p>
          </Stagger>
          <Stagger delay={0.14}>
            <p className="mt-4 max-w-2xl text-base lg:text-lg text-slate-700 font-body leading-relaxed">
              Solar Power Solutions, CCTV Surveillance, Electric Scooters, UPS &amp; Battery Systems, ELV Infrastructure and Smart Technology Solutions — with professional installation and reliable after-sales support.
            </p>
          </Stagger>

          {/* Trust checkmarks */}
          <Stagger delay={0.18}>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5 max-w-2xl" data-testid="hero-trust-points">
              {[
                "1000+ Happy Customers",
                "500+ Solar Installations",
                "1500+ CCTV Cameras Installed",
                "Expert Installation Team",
                "Genuine Warranty Products",
                "24×7 Technical Support",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-800 font-medium">
                  <Check size={16} weight="bold" className="text-emerald-500 shrink-0" /> {t}
                </div>
              ))}
            </div>
          </Stagger>

          <Stagger delay={0.22}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/contact" data-testid="hero-cta-survey" className="group inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-4 text-sm font-semibold hover:bg-slate-800 transition-colors">
                <Phone size={16} weight="bold" /> Get Free Site Survey
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-4 text-sm font-semibold hover:bg-emerald-600 transition-colors">
                <WhatsappLogo size={18} weight="fill" /> WhatsApp Now
              </a>
              <Link to="/contact" data-testid="hero-cta-quote" className="inline-flex items-center gap-2 border-2 border-slate-950 text-slate-950 px-6 py-4 text-sm font-semibold hover:bg-slate-950 hover:text-white transition-colors">
                <Lightning size={16} weight="fill" /> Request a Quote
              </Link>
            </div>
          </Stagger>

          {/* Slide indicator dots */}
          <div className="mt-10 hidden lg:flex items-center gap-3" data-testid="hero-slide-dots">
            {heroSlides.map((s, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                data-testid={`hero-slide-dot-${i}`}
                aria-label={`Slide ${i + 1}: ${s.label}`}
                className={`group flex items-center gap-2 transition-all ${i === slide ? "" : "opacity-50 hover:opacity-100"}`}
              >
                <span className={`h-[3px] transition-all duration-500 ${i === slide ? "w-12 bg-slate-950" : "w-6 bg-slate-400 group-hover:bg-slate-700"}`} />
                <span className={`text-[10px] font-bold uppercase tracking-[0.18em] ${i === slide ? "text-slate-950" : "text-slate-500"}`}>{s.label}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </section>

      {/* AUTHORIZED DEALER BRANDS */}
      <section className="bg-slate-950 text-white py-7 overflow-hidden" data-testid="dealer-brands-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="shrink-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold">Authorized Dealer of</div>
            <div className="text-xs text-slate-400 mt-1">Genuine products • Full warranty</div>
          </div>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex gap-10 animate-marquee whitespace-nowrap" style={{ width: "max-content" }}>
              {[...DEALER_BRANDS, ...DEALER_BRANDS, ...DEALER_BRANDS].map((b, i) => (
                <span key={i} className="font-heading font-bold text-xl lg:text-2xl text-slate-200 tracking-tight">{b}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TAGLINE / SEO BAND */}
      <section className="bg-amber-400 py-10" data-testid="tagline-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <h2 className="font-heading font-black text-2xl lg:text-4xl tracking-tighter text-slate-950">Smart Energy. Smart Security. Smart Future.</h2>
          <p className="text-sm lg:text-base text-slate-800 font-medium max-w-md">
            Solar | CCTV | UPS | Electric Scooters | ELV Solutions | Web &amp; AI — everything your home or business needs under one trusted technology partner.
          </p>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="bg-white py-24 lg:py-32" data-testid="divisions-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <div className="label-eyebrow">Business Divisions</div>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950 max-w-2xl">Five companies. <span className="italic font-light">One partner.</span></h2>
            </div>
            <p className="max-w-md text-slate-600 text-base lg:text-lg leading-relaxed">From rooftop solar to AI-powered surveillance — every solution under one roof, executed by a single accountable team.</p>
          </div>

          {(() => {
            const styles = {
              solar: { bg: "bg-amber-400", text: "text-slate-950", sub: "text-slate-800", arrow: "text-slate-950", h: "h-72 lg:h-80", iconBg: "bg-slate-950/15 border-slate-950/20" },
              cctv: { bg: "bg-slate-950", text: "text-white", sub: "text-slate-300", arrow: "text-amber-400", h: "h-72 lg:h-80", iconBg: "bg-white/20 border-white/30" },
              ev: { bg: "bg-emerald-500", text: "text-white", sub: "text-emerald-50", arrow: "text-white", h: "h-72", iconBg: "bg-white/20 border-white/30" },
              power: { bg: "bg-blue-600", text: "text-white", sub: "text-blue-50", arrow: "text-amber-300", h: "h-72", iconBg: "bg-white/20 border-white/30" },
              digital: { bg: "bg-violet-600", text: "text-white", sub: "text-violet-50", arrow: "text-amber-300", h: "h-72", iconBg: "bg-white/20 border-white/30" },
            };
            const renderCard = (d, i) => {
              const Icon = ICONS[d.icon];
              const s = styles[d.key];
              return (
                <Stagger key={d.key} delay={i * 0.05}>
                  <Link
                    to={d.href}
                    data-testid={`division-card-${d.key}`}
                    className={`relative ${s.bg} ${s.text} ${s.h} block overflow-hidden group transition-transform duration-300 hover:-translate-y-1`}
                  >
                    <div className="absolute inset-0 opacity-20 mix-blend-overlay">
                      <img src={d.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>
                    <div className="absolute -right-10 -bottom-10 w-48 h-48 lg:w-64 lg:h-64 rounded-full bg-white/10 group-hover:scale-125 transition-transform duration-700" />
                    <div className="absolute -left-6 -top-6 w-28 h-28 rounded-full bg-white/5" />

                    <div className="relative h-full flex flex-col justify-between p-7 lg:p-9">
                      <div className="flex items-start justify-between">
                        <div className={`inline-flex items-center justify-center w-12 h-12 lg:w-14 lg:h-14 backdrop-blur-sm border ${s.iconBg}`}>
                          <Icon size={26} weight="duotone" />
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${s.sub}`}>0{i + 1}</span>
                      </div>

                      <div>
                        <h3 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter leading-none">{d.title}</h3>
                        <p className={`mt-3 text-sm lg:text-[15px] leading-relaxed max-w-md ${s.sub}`}>{d.desc}</p>
                        <div className={`mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] ${s.arrow}`}>
                          Explore <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </Stagger>
              );
            };
            const row1 = DIVISIONS.filter((d) => d.key === "solar" || d.key === "cctv");
            const row2 = DIVISIONS.filter((d) => d.key === "ev" || d.key === "power" || d.key === "digital");
            return (
              <div className="space-y-5 lg:space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
                  <div className="lg:col-span-7">{renderCard(row1[0], 0)}</div>
                  <div className="lg:col-span-5">{renderCard(row1[1], 1)}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                  {row2.map((d, idx) => <div key={d.key}>{renderCard(d, idx + 2)}</div>)}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-slate-50 py-24 lg:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-12 lg:gap-16">
          <div className="md:col-span-5">
            <div className="label-eyebrow">Why TamilBTC</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Engineered for trust. Built to last.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Every installation passes a 32-point QC checklist. Every customer gets the founder's WhatsApp number. That's how we built 1000+ relationships.</p>
            <Link to="/about" data-testid="why-about-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-950 border-b-2 border-slate-950 pb-1">
              Read our story <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {[
              { icon: ShieldCheck, t: "Expert Team", d: "Certified engineers with 12+ years field experience." },
              { icon: Star, t: "Premium Products", d: "Tier-1 panels, Hikvision cameras, lithium batteries." },
              { icon: Wrench, t: "Professional Installation", d: "32-point QC, in-house crew, no subcontracting." },
              { icon: Lightbulb, t: "Fast Support", d: "24-48 hr response SLA across Tamil Nadu." },
              { icon: Buildings, t: "Affordable Pricing", d: "Transparent pricing, EMI options, no hidden costs." },
              { icon: Phone, t: "Founder Access", d: "Direct line to founder for escalations." },
            ].map((f, i) => (
              <div key={i} className="bg-white p-7" data-testid={`why-feature-${i}`}>
                <f.icon size={28} weight="duotone" className="text-amber-500" />
                <h4 className="mt-4 font-heading font-bold text-lg text-slate-950">{f.t}</h4>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="bg-white py-24 lg:py-32" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between lg:items-end mb-12 gap-6">
            <div>
              <div className="label-eyebrow">Featured Work</div>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950 max-w-2xl">500+ projects. <span className="italic font-light">Every one personal.</span></h2>
            </div>
            <Link to="/projects" className="hidden lg:inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-slate-950" data-testid="projects-view-all">View all <ArrowRight size={14} weight="bold" /></Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {PROJECTS.map((p, i) => (
              <Stagger key={p.id} delay={i * 0.05}>
                <div className={`relative card-flat overflow-hidden ${p.span} group`} data-testid={`project-card-${p.id}`}>
                  <div className="relative h-72 lg:h-96">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
                    <div className="absolute top-5 left-5 bg-white text-slate-950 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1">{p.category}</div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="font-heading text-xl lg:text-2xl text-white font-bold leading-tight">{p.title}</h3>
                    </div>
                  </div>
                </div>
              </Stagger>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-950 text-white py-24 lg:py-32 overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="label-eyebrow text-amber-400">Customer Stories</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter">Rated 4.9★ by Tamil Nadu's families and factories.</h2>
          </div>
        </div>
        <div className="mt-16 flex gap-6 animate-marquee" style={{ width: "max-content" }}>
          {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
            <div key={i} className="w-[360px] lg:w-[420px] shrink-0 bg-slate-900 border border-slate-800 p-8" data-testid={`testimonial-${i}`}>
              <Quotes size={28} weight="fill" className="text-amber-400" />
              <p className="mt-5 text-slate-200 leading-relaxed text-[15px]">{t.text}</p>
              <div className="mt-6 pt-5 border-t border-slate-800">
                <div className="font-semibold">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.role}</div>
                <div className="mt-2 flex gap-0.5">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={12} weight="fill" className="text-amber-400" />)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PARTNER */}
      <section className="bg-amber-400 py-24" data-testid="partner-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow text-slate-900">Partner With Us</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Grow with Tamil Nadu's fastest growing energy company.</h2>
            <p className="mt-5 text-slate-800 leading-relaxed max-w-lg">Franchise opportunities, dealer network and installation partners — across all 38 districts. High margins, factory pricing, full marketing support.</p>
            <Link to="/contact" data-testid="partner-cta" className="mt-8 inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3.5 text-sm font-semibold hover:bg-slate-800">
              Become a Partner <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-px bg-slate-900 border border-slate-900">
            {[
              { t: "Franchise", d: "Own a TamilBTC outlet" },
              { t: "Dealer", d: "Resell our products" },
              { t: "Installer", d: "Join our certified network" },
              { t: "Affiliate", d: "Refer & earn lifetime" },
            ].map((p, i) => (
              <div key={i} className="bg-amber-400 p-6" data-testid={`partner-type-${i}`}>
                <div className="font-heading font-bold text-xl text-slate-950">{p.t}</div>
                <div className="text-xs text-slate-800 mt-1">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bg-white py-24 lg:py-32" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="card-flat overflow-hidden">
              <img src={IMAGES.founder} alt="Founder" className="w-full h-[480px] object-cover object-top" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="label-eyebrow">Founder's Message</div>
            <Quotes size={40} weight="fill" className="mt-4 text-amber-400" />
            <p className="mt-6 font-heading text-2xl md:text-3xl text-slate-900 leading-snug tracking-tight max-w-2xl">
              "We started with one goal — bring affordable, reliable energy &amp; security technology to every home in Tamil Nadu. Twelve years later, we're still answering every customer call personally."
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="w-1 h-12 bg-amber-400" />
              <div>
                <div className="font-heading font-bold text-lg text-slate-950">{COMPANY.founder}</div>
                <div className="text-sm text-slate-500">{COMPANY.founderRole}, {COMPANY.name}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-slate-50 py-24 lg:py-32" data-testid="home-contact-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="label-eyebrow">Get Started</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">Let's build your smart future.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Tell us your need — solar, security, EV or all of the above. We'll send a senior engineer for a free site survey within 48 hours.</p>
            <div className="mt-8 space-y-3">
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 text-slate-900 font-semibold" data-testid="home-contact-phone">
                <Phone size={20} weight="duotone" className="text-amber-500" /> {COMPANY.phoneDisplay}
              </a>
              <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-900 font-semibold" data-testid="home-contact-whatsapp">
                <WhatsappLogo size={20} weight="duotone" className="text-emerald-500" /> Chat on WhatsApp
              </a>
            </div>
          </div>
          <div className="md:col-span-7">
            <LeadForm source="home" />
          </div>
        </div>
      </section>
    </div>
  );
}
